import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Plus, Trash, ArrowUpRight, Circuitry, Buildings, Sparkle,
  DownloadSimple, UploadSimple, ArrowsClockwise, CaretRight, CaretDown,
  SortAscending, SortDescending,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PANEL_TYPES = [
  { id: "electrical_room", title: "Electrical Room" },
  { id: "mcc", title: "Motor Control Center" },
  { id: "distribution_board", title: "Distribution Board" },
  { id: "lighting_control_panel", title: "Lighting Control Panel" },
  { id: "heat_trace_panel", title: "Heat Trace Panel" },
  { id: "control_panel", title: "Control Panel" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "",
    panel_type: "mcc",
    code_standard: "NEC",
    voltage: 480,
    phases: 3,
    main_breaker_amps: 400,
    location: "",
    org_id: "",
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [{ data: pj }, { data: og }] = await Promise.all([
        api.get("/projects"),
        api.get("/orgs"),
      ]);
      setProjects(pj);
      setOrgs(og);
    } catch (e) {
      toast.error("Failed to load projects");
    }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const createProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.org_id) delete payload.org_id;
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      setShowNew(false);
      nav(`/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  /** Group + sort state. Projects are bucketed by `panel_type`; each
   *  subgroup has its own sort field (name/date) and direction. */
  const [collapsed, setCollapsed] = useState({});            // archetype-id → bool
  const [sortByGroup, setSortByGroup] = useState({});         // archetype-id → { field, dir }

  /** Fire-and-forget "regenerate every deliverable" for a project. The
   *  user can then visit /deliverables to see the new files; existing
   *  files remain accessible while regeneration runs. */
  const updateDeliverables = async (p) => {
    try {
      await api.post(`/projects/${p.id}/deliverables/regenerate`);
      toast.success(`Updating deliverables for "${p.name}"… open the Deliverables tab to track progress.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start deliverables update");
    }
  };

  /** Bucket projects by panel_type in the same order as PANEL_TYPES (so
   *  the UI is stable regardless of the order returned by the API). */
  const grouped = useMemo(() => {
    const buckets = Object.fromEntries(PANEL_TYPES.map((t) => [t.id, []]));
    const orphans = [];
    (projects || []).forEach((p) => {
      if (buckets[p.panel_type]) buckets[p.panel_type].push(p);
      else orphans.push(p);
    });
    return { buckets, orphans };
  }, [projects]);

  const sortBucket = (id, list) => {
    const s = sortByGroup[id] || { field: "date", dir: "desc" };
    const dir = s.dir === "asc" ? 1 : -1;
    const get = (p) => {
      if (s.field === "name") return (p.name || "").toLowerCase();
      // date: prefer `updated_at`, fall back to `created_at`, then id (creation time embedded in ObjectId).
      return p.updated_at || p.created_at || p.id || "";
    };
    return [...list].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  };

  const toggleGroup = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const setGroupSort = (id, field) =>
    setSortByGroup((s) => {
      const cur = s[id] || { field: "date", dir: "desc" };
      const nx = cur.field === field
        ? { field, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { field, dir: field === "date" ? "desc" : "asc" };
      return { ...s, [id]: nx };
    });
  const exportProject = async (p) => {
    try {
      const res = await api.get(`/projects/${p.id}/export/json`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(p.name || "project").replace(/\s+/g, "-")}.eengdes.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${p.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  /** Import a `.eengdes.json` file as a new project owned by the signed-in
   *  user. Triggered by the hidden <input type=file> below. */
  const importFileRef = useRef(null);
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";       // allow re-importing the same file later
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/projects/import/json", payload);
      toast.success(`Imported as "${data.name}"`);
      // Refresh list and navigate into the new project.
      const list = await api.get("/projects");
      setProjects(list.data || []);
      nav(`/projects/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || (err instanceof SyntaxError ? "Not a valid JSON file" : "Import failed"));
    }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="dashboard" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {user?.role === "admin" && (
          <div className="mb-6 border border-[#FFCC00]/30 bg-[#FFCC00]/5 px-4 py-2.5 flex items-center gap-3 text-xs font-mono text-[#FFCC00]"
               data-testid="admin-studio-banner">
            <span className="label-tag !text-[#FFCC00]/80">// admin studio</span>
            <span className="text-[#FFCC00]/70">
              Full 2D/3D panel-design workspace visible only to admin. Professional Engineers and Clients see the project list, tools, and deliverables but not the panel layout editor / 3D room view.
            </span>
          </div>
        )}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-tag mb-2">// Studio</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Your projects</h1>
            <p className="text-gray-400 mt-2">Open or create a new panel project to begin designing.</p>
          </div>
          <div className="flex gap-2">
            <input ref={importFileRef} type="file" accept=".json,application/json"
                   className="hidden" onChange={onImportFile}
                   data-testid="import-project-file" />
            <button
              onClick={() => importFileRef.current?.click()}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="import-project-btn"
              title="Import a project from a .eengdes.json file (e.g. exported from another environment)"
            >
              <UploadSimple weight="bold" size={18} /> Import project
            </button>
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/projects/sample");
                  toast.success("Sample project created");
                  nav(`/projects/${data.id}`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not create sample");
                }
              }}
              className={`btn-ghost inline-flex items-center gap-2 ${user?.role === "admin" ? "" : "hidden"}`}
              data-testid="sample-project-btn"
              title="Admin-only: seed the 30x20 electrical-room demo"
            >
              <Sparkle weight="fill" size={18} /> Load sample project
            </button>
            <button
              onClick={() => setShowNew(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="new-project-btn"
            >
              <Plus weight="bold" size={18} /> New project
            </button>
          </div>
        </div>

        {/* Grid — grouped by panel archetype, sortable per subgroup */}
        <div className="mt-10">
          {projects.length === 0 ? (
            <div className="bp-grid border border-white/10 p-16 text-center" data-testid="empty-state">
              <Circuitry size={48} className="text-[#00E5FF] mx-auto" weight="duotone" />
              <h2 className="font-display text-2xl font-bold mt-4">No projects yet</h2>
              <p className="text-gray-400 mt-2">Create your first panel project — it takes 30 seconds.</p>
              <button
                onClick={() => setShowNew(true)}
                className="btn-primary mt-6 inline-flex items-center gap-2"
                data-testid="empty-state-cta"
              >
                <Plus size={18} weight="bold" /> Create project
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {PANEL_TYPES.map((archetype) => {
                const list = grouped.buckets[archetype.id] || [];
                if (list.length === 0) return null;
                const isCollapsed = !!collapsed[archetype.id];
                const s = sortByGroup[archetype.id] || { field: "date", dir: "desc" };
                const sortedList = sortBucket(archetype.id, list);
                return (
                  <section key={archetype.id} className="border border-white/10 bg-[#0F1218]"
                            data-testid={`projects-group-${archetype.id}`}>
                    <header className="px-4 py-3 flex items-center gap-3 bg-[#12151C] border-b border-white/10">
                      <button
                        onClick={() => toggleGroup(archetype.id)}
                        className="text-gray-400 hover:text-white p-1"
                        data-testid={`projects-group-toggle-${archetype.id}`}
                      >
                        {isCollapsed ? <CaretRight size={16} /> : <CaretDown size={16} />}
                      </button>
                      <h2 className="font-display text-lg font-bold text-white flex-1">
                        {archetype.title}
                        <span className="ml-2 label-tag text-[10px]">{list.length}</span>
                      </h2>
                      <div className="flex items-center gap-2">
                        <span className="label-tag text-[10px]">Sort</span>
                        {[{id:"name",label:"Name"},{id:"date",label:"Date"}].map((f) => (
                          <button
                            key={f.id}
                            onClick={() => setGroupSort(archetype.id, f.id)}
                            className={`text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1 px-2 py-0.5 border transition
                              ${s.field === f.id ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-gray-400 hover:text-white"}`}
                            data-testid={`projects-sort-${archetype.id}-${f.id}`}
                          >
                            {f.label}
                            {s.field === f.id && (s.dir === "asc" ? <SortAscending size={11} /> : <SortDescending size={11} />)}
                          </button>
                        ))}
                      </div>
                    </header>
                    {!isCollapsed && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                        {sortedList.map((p) => (
                          <div
                            key={p.id}
                            className="bg-[#12151C] p-6 hover:bg-[#1A1F29] transition relative tick-corner"
                            data-testid={`project-card-${p.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="label-tag flex items-center gap-2">
                                  {p.org_id ? (<><Buildings size={10} /> Org</>) : "Personal"} · {p.code_standard}
                                </div>
                                <h3 className="font-display text-xl font-bold mt-2 text-white">{p.name}</h3>
                              </div>
                              <button
                                onClick={() => remove(p.id)}
                                className="text-gray-500 hover:text-red-400 transition"
                                title="Delete"
                                data-testid={`delete-project-${p.id}`}
                              >
                                <Trash size={18} />
                              </button>
                            </div>
                            <div className="mt-4 font-mono text-xs text-gray-400 space-y-1">
                              <div>System: <span className="text-white">{p.voltage}V / {p.phases}φ</span></div>
                              <div>Main: <span className="text-white">{p.main_breaker_amps}A</span></div>
                              <div>Circuits: <span className="text-[#00E5FF]">{(p.circuits || []).length}</span></div>
                            </div>
                            <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
                              <Link
                                to={`/projects/${p.id}`}
                                className="inline-flex items-center gap-2 text-[#00E5FF] hover:text-white font-mono text-sm"
                                data-testid={`open-project-${p.id}`}
                              >
                                Open builder <ArrowUpRight weight="bold" size={16} />
                              </Link>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => updateDeliverables(p)}
                                  className="text-gray-500 hover:text-[#00E5FF] transition font-mono text-xs inline-flex items-center gap-1"
                                  title="Regenerate PDF · DXF · BOM · Single-line · Equipment & cable schedules — see the Deliverables tab to track progress"
                                  data-testid={`update-deliverables-${p.id}`}
                                >
                                  <ArrowsClockwise size={14} /> Update Deliverables
                                </button>
                                <button
                                  onClick={() => exportProject(p)}
                                  className="text-gray-500 hover:text-[#FFCC00] transition font-mono text-xs inline-flex items-center gap-1"
                                  title="Download project as JSON — useful for backups or moving to another environment"
                                  data-testid={`export-project-${p.id}`}
                                >
                                  <DownloadSimple size={14} /> Export
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
              {grouped.orphans.length > 0 && (
                <section className="border border-white/10 bg-[#0F1218]" data-testid="projects-group-other">
                  <header className="px-4 py-3 bg-[#12151C] border-b border-white/10">
                    <h2 className="font-display text-lg font-bold text-white">Other</h2>
                  </header>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                    {grouped.orphans.map((p) => (
                      <div key={p.id} className="bg-[#12151C] p-6 hover:bg-[#1A1F29] transition relative tick-corner"
                            data-testid={`project-card-${p.id}`}>
                        <div className="label-tag">{p.panel_type}</div>
                        <h3 className="font-display text-xl font-bold mt-2 text-white">{p.name}</h3>
                        <Link to={`/projects/${p.id}`} className="text-[#00E5FF] font-mono text-sm mt-4 inline-block"
                              data-testid={`open-project-${p.id}`}>Open builder →</Link>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New project modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-project-modal">
          <form
            onSubmit={createProject}
            className="bg-[#12151C] border border-white/10 w-full max-w-lg p-6"
          >
            <div className="label-tag">// Create project</div>
            <h2 className="font-display text-2xl font-bold mt-2">New panel project</h2>

            <label className="block mt-5">
              <span className="label-tag">Project name</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                data-testid="new-project-name"
                placeholder="MCC-A Building 3"
              />
            </label>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <label className="block">
                <span className="label-tag">Panel type</span>
                <select
                  value={form.panel_type}
                  onChange={(e) => setForm({ ...form, panel_type: e.target.value })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-panel-type"
                >
                  {PANEL_TYPES.map((t) => (<option key={t.id} value={t.id}>{t.title}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="label-tag">Code standard</span>
                <select
                  value={form.code_standard}
                  onChange={(e) => setForm({ ...form, code_standard: e.target.value })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-code"
                >
                  <option value="NEC">NEC (US)</option>
                  <option value="CEC">CEC (Canada)</option>
                  <option value="IEEE">IEEE</option>
                </select>
              </label>
              <label className="block">
                <span className="label-tag">System voltage</span>
                <select
                  value={form.voltage}
                  onChange={(e) => setForm({ ...form, voltage: Number(e.target.value) })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-voltage"
                >
                  {[120, 208, 240, 277, 480, 600].map((v) => <option key={v} value={v}>{`${v}V`}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label-tag">Phases</span>
                <select
                  value={form.phases}
                  onChange={(e) => setForm({ ...form, phases: Number(e.target.value) })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-phases"
                >
                  <option value={1}>{`1φ`}</option>
                  <option value={3}>{`3φ`}</option>
                </select>
              </label>
              <label className="block">
                <span className="label-tag">Main breaker (A)</span>
                <input
                  type="number"
                  value={form.main_breaker_amps}
                  onChange={(e) => setForm({ ...form, main_breaker_amps: Number(e.target.value) })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-main"
                />
              </label>
              <label className="block">
                <span className="label-tag">Location</span>
                <input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-location"
                  placeholder="Plant 3 — Substation B"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="label-tag">Workspace</span>
                <select
                  value={form.org_id}
                  onChange={(e) => setForm({ ...form, org_id: e.target.value })}
                  className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="new-project-org"
                >
                  <option value="">Personal (only you)</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name} — {o.member_count} members</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowNew(false)}
                className="btn-ghost"
                data-testid="new-project-cancel"
              >Cancel</button>
              <button type="submit" className="btn-primary" disabled={creating} data-testid="new-project-create">
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
